import { DataTable as PrimeReactDataTable } from 'primereact/datatable';
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';

import moment from 'moment';
import { InputText } from 'primereact/inputtext';
import { ReactComponent as CloseSmallIcon } from '../../assets/images/close_small-icon.svg';
import filterIcon from '../../assets/images/filter-edit.svg';
import { ReactComponent as SearchIcon } from '../../assets/images/search-icon.svg';
import sortDownIcon from '../../assets/images/datatable/sort-desc.svg';
import sortIcon from '../../assets/images/sorting-icon.svg';
import sortUpIcon from '../../assets/images/datatable/sort-asc.svg';

import { Chip } from 'primereact/chip';
import { useTranslation } from 'react-i18next';
import { Constants } from '../../utils/Constants';
import FilterPanel from '../filterpanel/FilterPanel';
import './DataTable.scss';

import { Column } from 'primereact/column';
import { DataTableContext } from './DataTableContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';

export const FILTER_TYPES = {
    LIST: 'LIST',
    TEXT: 'TEXT',
    DATE_RANGE: 'DATE_RANGE',
    DATE_TIME_RANGE: 'DATE_TIME_RANGE',
    NUMBER_RANGE: 'NUMBER_RANGE',
    AMOUNT: 'AMOUNT',
    BOOLEAN: 'BOOLEAN'
};


/**
 * 
 * @param filterConfig konfiguracja filtrów: tablica obiektów: {fieldName - nazwa pola do filtrowania ustawiona w Column::field, label - nazwa pola, type - typ filtra (FILTER_TYPE), params - parametry wymagane do wyświetlenia panelu z filtrem (np dla typu LIST - lista opcji do wyboru)},
 * apiParamName - nazwa parametru w rest-api, apiValueConverter - konwerter użyty do przygotowania wartości wysłanej do rest-api (dla type=DATE_RANGE mogą być dwa konwertery: apiValueFromConverter i apiValueToConverter)
 * @param fetchData - funkcja zwracają stronnicowane dane; zwraca stronę (data) + ilość wszystkich rekordów (totalRecords); parametrem są kryteria wyszukiwania: (searchCriteria) 
 * @params children - kolumny tabeli
 * @params selectable - czy tabela ma być możliwość zaznaczania rekordów (domyślnie false)
 * @params onSelectionChange - funkcja wywoływana przy zmianie zaznaczenia rekordów (domyślnie undefined); parametrem jest lista zaznaczonych rekordów
 * @params globalSearchPosition - pozycja globalnego wyszukiwania: left, right, none (domyślnie 'left')
 * komponent można odświeżyć przez forwardRef udostępnioną funkcją: refresh()
 * @returns 
 */
const DataTable = forwardRef(({
    filterConfig, fetchData, tableToolbarLeft, tableToolbarRight, tableToolbarBottom,
    selectable = false, onSelectionChange, onRowSelect, onRowUnselect, onRowClick,
    rowClassName, selectedRowId,
    globalSearchPosition = 'left',
    dataTableContext = DataTableContext,
    minWidth = '800px',
    showRowsPerPage = true,
    showPaginator = true,
    children, ...rest
}, ref) => {

    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInputTooltip, setPageInputTooltip] = useState(t('dataTable.pageInputTooltip'));
    const filterDialog = useRef(null);

    //kryteria wyszukiwania
    const { searchCriteria, setSearchCriteria } = useContext(dataTableContext);
    // const searchCriteriaRef = useRef(searchCriteria);


    const refresh = (params = { clearSelections: false }) => {
        if (params.clearSelections) {
            setSelectedAll(false);
            setSelectionExceptions([]);
        }
        setSearchCriteria(prev => ({ ...prev, timestamp: new Date().getTime() }));
    };


    useImperativeHandle(ref, () => ({
        refresh,
        clearSelection,
        selectedAll: selectedAll,
        selectionExceptions: selectionExceptions,
        decorateCriteria: decorateCriteria,
        getTotalRecords: () => totalRecords
    }));

    //wyszukiwanie po wszyskim, pole nad tabelką
    const [baseQuery, setBaseQuery] = useState();

    // Synchronizuj baseQuery z searchCriteria po inicjalizacji
    useEffect(() => {
        if (searchCriteria && searchCriteria.baseQuery !== undefined) {
            setBaseQuery(searchCriteria.baseQuery);
        }

    }, [searchCriteria]);

    // Synchronizuj currentPage z searchCriteria.first i rows przy inicjalizacji lub zmianie strony
    useEffect(() => {
        if (searchCriteria && typeof searchCriteria.first === 'number' && typeof searchCriteria.rows === 'number') {
            setCurrentPage(Math.floor(searchCriteria.first / searchCriteria.rows) + 1);
        }
    }, [searchCriteria?.first, searchCriteria?.rows]);

    // Resetuj currentPage do 1 tylko przy faktycznej zmianie baseQuery (nie przy inicjalizacji z URL)
    const prevBaseQueryRef = useRef();
    useEffect(() => {
        if (
            searchCriteria &&
            searchCriteria.baseQuery !== undefined &&
            prevBaseQueryRef.current !== undefined &&
            searchCriteria.baseQuery !== prevBaseQueryRef.current
        ) {
            setCurrentPage(1);
        }
        prevBaseQueryRef.current = searchCriteria ? searchCriteria.baseQuery : undefined;
    }, [searchCriteria?.baseQuery]);

    const serializeCriteriaWithoutPaginationAndSort = (criteria) => {
        if (criteria) {
            const sc = { ...criteria };
            delete sc.first;
            delete sc.rows;
            delete sc.sortField;
            delete sc.sortOrder;
            return JSON.stringify(sc);
        } else {
            return JSON.stringify(criteria);
        }
    }

    useEffect(() => {
        //jeśli zmieniły się kryteria wyszukiwania, to resetujemy zaznaczenia
        setSelectionExceptions([]);
    }, [serializeCriteriaWithoutPaginationAndSort(searchCriteria)]);

    const baseQueryTimeoutRef = useRef(null);

    //dane - bieąca strona
    const [data, setData] = useState([]);

    //dane - ilość wszystkich rekordów
    const [totalRecords, setTotalRecords] = useState(0);

    //dane panelu filtrowanie/sortowanie
    const [filterPanelState, setFilterPanelState] = useState({});

    //pozycję myszy zapamiętujemy, zeby okienko z filtrem wyswietliło się pod elementem, w który klikamy
    const lastMousePosition = useRef({ x: 0, y: 0 });

    const decorateCriteria = (criteria) => {
        if (!criteria) {
            return null;
        }
        const sc = JSON.parse(JSON.stringify(criteria));
        if (sc.filters) {
            Object.keys(sc.filters).forEach(filterName => {
                const filter = sc.filters[filterName];
                const fieldConfig = filterConfig.find(fc => fc.fieldName === filterName);
                if (fieldConfig) {
                    filter.apiParamName = fieldConfig.apiParamName || filterName;
                    filter.apiValueConverter = fieldConfig.apiValueConverter;
                    filter.apiValueFromConverter = fieldConfig.apiValueFromConverter;
                    filter.apiValueToConverter = fieldConfig.apiValueToConverter;
                }
            })
        }
        return sc;
    }

    //zmiana searchCriteria wymusza odswiezenie danych
    useEffect(() => {
        const sc = decorateCriteria(searchCriteria);
        if (searchCriteria && sc) {

            const totalPages = Math.ceil(totalRecords / sc.rows);
            const currentPage = Math.floor(sc.first / sc.rows) + 1;
            // Nie pobieraj danych, jeśli currentPage wykracza poza zakres
            if (totalPages > 0 && currentPage > totalPages) {
                return;
            }
            setLoading(true);
            fetchData(sc)
                .then(resp => {
                    setData(normalizeDataDates(resp?.data || []));
                    setTotalRecords(resp?.totalRecords || 0);
                    setLoading(false);
                }).catch(err => {
                    setData([]);
                    setTotalRecords(0);
                    setLoading(false);
                })
        }
    }, [searchCriteria]);

    //po chwili od zmiany baseQuery rozpoczynamy wyszukiwanie
    useEffect(() => {
        if (baseQuery != null) {
            if (baseQueryTimeoutRef.current) {
                clearTimeout(baseQueryTimeoutRef.current);
            }
            baseQueryTimeoutRef.current = setTimeout(() => {
                filterByBaseQuery();
            }, 1000);
        }

    }, [baseQuery])

    useEffect(() => {
        updateSelections();
    }, [data]);


    //zamieniamy daty ze stringa na Date
    const normalizeDataDates = (data) => {
        const isValidDate = (str) => {
            if (typeof str !== 'string') return false;
            const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
            const dateTimeZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
            if (dateOnly.test(str) || dateTimeZ.test(str)) {
                return moment(str, moment.ISO_8601, true).isValid();
            }
            return false;
        };
        return data?.map(item => {
            const normalized = { ...item };
            for (let key in normalized) {
                const value = normalized[key];
                if (typeof value === 'string' && isValidDate(value)) {
                    normalized[key] = moment.utc(value).toDate();
                }
            }
            return normalized;
        }) || [];
    };



    useEffect(() => {
        const handleMouseMove = (e) => {
            lastMousePosition.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    //kliknięcie w nagłówek tabeli generuje zdarzenie onSort, w którym ustawiamy timestamp i zapamiętujemy jaka kolumna była kliknięta;
    //zmiana pola timestamp powoduje wyświetlenie okienka z filtrami: FilterPanel
    useEffect(() => {
        filterDialog.current.hide();
        if (filterPanelState.timestamp && filterPanelState.timestamp.getTime() > new Date() - 3000) {
            const { x, y } = lastMousePosition.current;
            const target = document.elementFromPoint(x, y);
            if (target) {
                filterDialog.current.toggle({
                    currentTarget: target,
                    preventDefault: () => { },
                    stopPropagation: () => { }
                });
            }
        }
    }, [filterPanelState.timestamp]);


    const filterByBaseQuery = () => {
        if (baseQuery !== searchCriteria?.baseQuery) {
            setSearchCriteria(prev => ({ ...prev, baseQuery: baseQuery, first: 0 }));
        }
    }


    const onPageInputKeyDown = (event, options) => {
        if (event.key === 'Enter') {
            const page = parseInt(currentPage);
            const totalPages = options.props.totalPages;
            if (!page || page < 1 || page > totalPages) {
                setPageInputTooltip(t('dataTable.pageInputErrorTooltip', { totalPages }));
                if (page > 0) {
                    setCurrentPage(totalPages);
                    const _first = searchCriteria.rows * (totalPages - 1);
                    setSearchCriteria(prev => ({ ...prev, first: _first }));
                } else {
                    setCurrentPage(1);
                    setSearchCriteria(prev => ({ ...prev, first: 0 }));
                }
            } else {
                const _first = currentPage ? searchCriteria.rows * (page - 1) : 0;
                setSearchCriteria(prev => ({ ...prev, first: _first }));
                setPageInputTooltip(t('dataTable.pageInputTooltip'));
            }
        }
    };

    const onPageChange = (e) => {
        setSearchCriteria(prev => ({ ...prev, first: e.first, rows: e.rows }));
        setCurrentPage(e.page + 1);
    };

    const onPageInputChange = (event) => {
        setCurrentPage(event.target.value);
    };


    const handleSort = (sortAsc) => {
        setSearchCriteria(prev => ({ ...prev, sortField: filterPanelState.fieldName, sortOrder: sortAsc ? 1 : -1 }))
        filterDialog.current.hide();
    }

    const clearSelection = () => {
        setSelectedAll(false);
        setSelectionExceptions([]);
        setSelected([]);
    };

    const applyFilter = (type, params) => {
        const fieldName = filterPanelState.fieldName;
        const fieldConfig = filterConfig.find(fc => fc.fieldName === fieldName);
        setSearchCriteria(prev => {
            const sc = {
                ...prev,
                first: 0,
                filters: {
                    ...prev.filters,
                    [fieldName]: {
                        type: type,
                        filterNameLabel: fieldConfig?.label,
                        value: params
                    }
                }
            }
            let toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.LIST && (sc.filters[fieldName].value == null || sc.filters[fieldName].value.length === 0));
            toRemove.forEach(fieldName => delete sc.filters[fieldName]);
            toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.TEXT && (sc.filters[fieldName].value == null || sc.filters[fieldName].value.trim() === ''));
            toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.BOOLEAN && (sc.filters[fieldName].value == null));
            toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.DATE_RANGE && (sc.filters[fieldName].value == null || (sc.filters[fieldName].value.from == null && sc.filters[fieldName].value.to == null)));
            toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.DATE_TIME_RANGE && (sc.filters[fieldName].value == null || (sc.filters[fieldName].value.from == null && sc.filters[fieldName].value.to == null)));            
            toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.NUMBER_RANGE && (sc.filters[fieldName].value == null || (sc.filters[fieldName].value.from == null && sc.filters[fieldName].value.to == null)));
            toRemove.forEach(fieldName => delete sc.filters[fieldName]);
            toRemove = Object.keys(sc.filters).filter(fieldName => sc.filters[fieldName].type === FILTER_TYPES.AMOUNT && (sc.filters[fieldName].value == null || (sc.filters[fieldName].value.from == null && sc.filters[fieldName].value.to == null)));
            toRemove.forEach(fieldName => delete sc.filters[fieldName]);
            return sc;
        });
    }

    const prepareFiltersToDisplay = () => {
        let result = [];
        if (searchCriteria?.filters) {
            Object.keys(searchCriteria.filters).forEach(filterName => {
                const filter = searchCriteria.filters[filterName];
                const nameLabel = filter.filterNameLabel;
                if (filter.type === FILTER_TYPES.LIST) {
                    const list = filter.value;
                    list.forEach(item => {
                        result.push({ name: filterName, nameLabel, value: item, valueLabel: item.label })
                    })
                } else if (filter.type === FILTER_TYPES.TEXT) {
                    result.push({ name: filterName, nameLabel, value: filter.value, valueLabel: filter.value })
                } else if (filter.type === FILTER_TYPES.DATE_RANGE) {
                    if (filter.value?.from || filter.value?.to) {
                        let filterValueLabel;
                        if (filter.value?.from && filter.value?.to) {
                            filterValueLabel = `${moment(filter.value.from).format(Constants.DATE_FORMAT)} - ${moment(filter.value.to).format(Constants.DATE_FORMAT)}`;
                        } else if (filter.value?.from) {
                            filterValueLabel = `${t('filterPanel.dateFrom')} ${moment(filter.value.from).format(Constants.DATE_FORMAT)}`;
                        } else {
                            filterValueLabel = `${t('filterPanel.dateTo')} ${moment(filter.value.to).format(Constants.DATE_FORMAT)}`;
                        }
                        result.push({ name: filterName, nameLabel, value: filter.value, valueLabel: filterValueLabel })
                    }

                } else if (filter.type === FILTER_TYPES.DATE_TIME_RANGE) {
                    if (filter.value?.from || filter.value?.to) {
                        let filterValueLabel;
                        if (filter.value?.from && filter.value?.to) {
                            filterValueLabel = `${moment(filter.value.from).format(Constants.DATE_TIME_FORMAT)} - ${moment(filter.value.to).format(Constants.DATE_TIME_FORMAT)}`;
                        } else if (filter.value?.from) {
                            filterValueLabel = `${t('filterPanel.dateFrom')} ${moment(filter.value.from).format(Constants.DATE_TIME_FORMAT)}`;
                        } else {
                            filterValueLabel = `${t('filterPanel.dateTo')} ${moment(filter.value.to).format(Constants.DATE_TIME_FORMAT)}`;
                        }
                        result.push({ name: filterName, nameLabel, value: filter.value, valueLabel: filterValueLabel })
                    }

                } else if (filter.type === FILTER_TYPES.NUMBER_RANGE) {
                    if (filter.value?.from != null || filter.value?.to != null) {
                        let filterValueLabel;
                        if (filter.value?.from != null && filter.value?.to != null) {
                            filterValueLabel = `${filter.value.from} - ${filter.value.to}`;
                        } else if (filter.value?.from != null) {
                            filterValueLabel = `${t('filterPanel.numberFrom')} ${filter.value.from}`;
                        } else {
                            filterValueLabel = `${t('filterPanel.numberTo')} ${filter.value.to}`;
                        }
                        result.push({ name: filterName, nameLabel, value: filter.value, valueLabel: filterValueLabel })
                    }

                } else if (filter.type === FILTER_TYPES.AMOUNT) {
                    if (filter.value?.from != null || filter.value?.to != null) {
                        let filterValueLabel;
                        if (filter.value?.from != null && filter.value?.to != null) {
                            filterValueLabel = `${filter.value.from} - ${filter.value.to}`;
                        } else if (filter.value?.from != null) {
                            filterValueLabel = `${t('filterPanel.amountFrom')} ${filter.value.from}`;
                        } else {
                            filterValueLabel = `${t('filterPanel.amountTo')} ${filter.value.to}`;
                        }
                        result.push({ name: filterName, nameLabel, value: filter.value, valueLabel: filterValueLabel?.replaceAll('.', ',') })
                    }
                } else if (filter.type === FILTER_TYPES.BOOLEAN) {
                    result.push({ name: filterName, nameLabel, value: filter.value, valueLabel: RenderedHelper.formatBoolean(filter.value) })
                }
            })
        }
        return result;
    }

    const activeFilters = prepareFiltersToDisplay();

    const removeFilter = (filter) => {
        const sc = { ...searchCriteria, first: 0 };
        const f = sc.filters[filter.name];
        if (f.type === FILTER_TYPES.LIST) {
            f.value = f.value.filter(v => v.id !== filter.value.id);
        } else if (f.type === FILTER_TYPES.TEXT) {
            delete sc.filters[filter.name];
        } else if (f.type === FILTER_TYPES.DATE_RANGE) {
            delete sc.filters[filter.name];
        } else if (f.type === FILTER_TYPES.DATE_TIME_RANGE) {
            delete sc.filters[filter.name];
        } else if (f.type === FILTER_TYPES.NUMBER_RANGE) {
            delete sc.filters[filter.name];
        } else if (f.type === FILTER_TYPES.AMOUNT) {
            delete sc.filters[filter.name];
        } else if (f.type === FILTER_TYPES.BOOLEAN) {
            delete sc.filters[filter.name];
        }
        setSearchCriteria(sc);
    }

    /**
     * metoda dla parametru filtra uzywanego w FieldPanel ustawia wybrane wczesniej wartosci, tak zeby otworzenie FieldPanel prezentowalo, to co bylo wczesniej wybrane; 
     * @param {*} fieldName nazwa pola po którym filtrujemy/sortujemy
     * @param {*} type typ filtra FILTER_TYPES
     * @param {*} filterParams parametry filtra, zalezne od typu; np dla LIST - lista wszystkich mozliwych wartości; dla TEXT - tekst po którym szukamy
     * @returns 
     */
    const enhanceFilterParamsWithCurrentSelection = (fieldName, type, filterParams) => {
        const filterValue = searchCriteria?.filters?.[fieldName];

        switch (type) {
            case FILTER_TYPES.LIST:
                if (!filterParams || !Array.isArray(filterParams)) {
                    return [];
                }
                const selectedItems = filterValue?.value || [];
                return filterParams.map(item => ({
                    ...item,
                    checked: Array.isArray(selectedItems) && selectedItems.some(fv => fv.id === item.id)
                }));
            case FILTER_TYPES.TEXT:
                return filterValue || '';
            case FILTER_TYPES.DATE_RANGE:
                return {...filterValue?.value, minDate: filterParams?.minDate, maxDate: filterParams?.maxDate } || { from: null, to: null };
            case FILTER_TYPES.DATE_TIME_RANGE:
                return filterValue?.value || { from: null, to: null };
            case FILTER_TYPES.NUMBER_RANGE: {
                const config = filterConfig.find(fc => fc.fieldName === fieldName);
                return {
                    ...filterValue?.value || { from: null, to: null },
                    maxValue: config?.params?.maxValue
                };
            }
            case FILTER_TYPES.AMOUNT: {
                const config = filterConfig.find(fc => fc.fieldName === fieldName);
                return {
                    ...filterValue?.value || { from: null, to: null },
                    maxValue: config?.params?.maxValue
                };
            }
            case FILTER_TYPES.BOOLEAN: {
                return filterValue?.value !== undefined && filterValue?.value !== null ? filterValue.value : null;
            }
            default:
                return filterParams;
        }
    }

    //lista zaznaczonych obiektów na stronie (obiektów, nie id)
    const [selected, setSelected] = useState([]);

    //id rekordów, które nie są wyjątkiem od zaznaczenia globalnego, czyli gdy jest wybrana opcja "zaznacz wszystkie"
    //to lista zawiera odznaczenia, a gdy nie jest wybrana opcja "zaznacz wszystkie", to lista zawiera odznaczenia
    const [selectionExceptions, setSelectionExceptions] = useState([]);

    //stan przełącznika "zaznacz wszystkie"
    const [selectedAll, setSelectedAll] = useState(false);
    const selectedAllRef = useRef(selectedAll);

    // Aktualizuj ref gdy zmienia się selectedAll
    useEffect(() => {
        selectedAllRef.current = selectedAll;
    }, [selectedAll]);


    //ustawia zaznaczenie dla bieącej strony
    useEffect(() => {
        setSelected(selectedAll ? data : []);
        setSelectionExceptions([]);
    }, [selectedAll]);


    const updateSelections = () => {
        if (selectedAll) {
            setSelected(data.filter(row => !selectionExceptions.includes(row.id)));
        } else {
            setSelected(data.filter(row => selectionExceptions.includes(row.id)));
        }
    }

    const handleSelectionChange = (e) => {
        // ID wszystkich elementów na bieżącej stronie
        const currentPageIds = data.map(item => item.id);

        let exceptions;
        //selectedAll - zdarzało się, ze miało wartość z mementu tworzenia komponentu, a nie z mementu zmiany zaznaczenia
        if (selectedAllRef.current) {
            // Gdy "zaznacz wszystkie" jest włączone, wyjątki to elementy odznaczone na tej stronie
            exceptions = data.filter(item => !e.value.includes(item)).map(item => item.id);
        } else {
            // Gdy "zaznacz wszystkie" jest wyłączone, wyjątki to elementy zaznaczone na tej stronie
            exceptions = data.filter(item => e.value.includes(item)).map(item => item.id);
        }
        setSelectionExceptions(prev => {
            // Usuwamy wyjątki z poprzednich stron, które nie są już aktualne
            const exceptionsFromOtherPages = prev.filter(id => !currentPageIds.includes(id));
            // Dodajemy nowe wyjątki z bieżącej strony
            const newExceptions = [...exceptionsFromOtherPages, ...exceptions];
            return newExceptions;
        });
        setSelected(e.value);
    };

    useEffect(() => {
        if (onSelectionChange) {
            let selectedCount = 0;
            if (selectedAll) {
                selectedCount = totalRecords - selectionExceptions.length;
            } else {
                selectedCount = selectionExceptions.length;
            }
            onSelectionChange(selectedCount);
        }
    }, [selected]);

    const globalSearch = (
        <span className="p-input-icon-left">
            <InputText variant="filled" className='search-input small' placeholder={t('dataTable.search')} value={baseQuery} onChange={e => {
                setBaseQuery(e.target.value);
            }} />
            <SearchIcon />
        </span>
    )

    const isTableToolbarRendered = () => {
        return tableToolbarLeft || tableToolbarRight || tableToolbarBottom || globalSearchPosition !== 'none';
    }

    return (
        <>
            <FilterPanel
                onSortAsc={() => handleSort(true)}
                onSortDesc={() => handleSort(false)}
                type={filterPanelState.filterType}
                params={filterPanelState.filterParams}
                applyFilter={applyFilter}
                ref={filterDialog}
                timestamp={filterPanelState.timestamp}
            />
            <div className="data-table">
                {isTableToolbarRendered() && (
                    <div className='table-toolbar'>
                        <div className='pt-2 pb-2 flex gap-3'>
                            {globalSearchPosition === 'left' && globalSearch}
                            <div>
                                {tableToolbarLeft}
                            </div>
                        </div>
                        <div className='ml-auto pt-2 pb-2 flex gap-3'>
                            {globalSearchPosition === 'right' && globalSearch}
                            {tableToolbarRight}
                        </div>


                    </div>
                )}
                {tableToolbarBottom && (
                    <>
                        <div className='separator' />
                        <div className='table-toolbar pb-1'>
                            <div className='pt-2 pb-2 flex gap-3'>
                                {tableToolbarBottom}
                            </div>
                        </div>
                    </>
                )}


                {(isTableToolbarRendered() || tableToolbarBottom) && <div className='separator' />}
                <div className='table-wrapper'>
                    {activeFilters.length > 0 && (
                        <div className='filters'>
                            <span className='label'>{t('dataTable.filters')}</span>
                            {activeFilters.map((filter, index) => (
                                <FilterItem label={filter.nameLabel + ': ' + filter.valueLabel} removable onClick={() => {
                                    removeFilter(filter);
                                }} key={`filter-${index}`} />
                            ))}

                            <FilterItem className='clear' label={t('dataTable.clearAll')} key={`filter-clear-all`}
                                onClick={() => {
                                    setSearchCriteria(prev => ({ ...prev, first: 0, filters: {} }))
                                }}
                            />
                        </div>
                    )}

                    <PrimeReactDataTable
                        dataKey='id'
                        {...rest}
                        tableStyle={{ minWidth: minWidth }}
                        value={data}
                        totalRecords={totalRecords}
                        loading={loading}
                        paginator={totalRecords > 0 && showPaginator}
                        rows={searchCriteria?.rows}
                        rowsPerPageOptions={showRowsPerPage ? [10, 20, 50] : []}
                        first={searchCriteria?.first}
                        paginatorClassName="paginator"
                        lazy={true}
                        onPage={onPageChange}
                        emptyMessage={t('dataTable.emptyMessage')}
                        rowClassName={rowClassName || (selectedRowId ? (rowData) => {
                            return rowData.id === selectedRowId ? 'p-highlight' : '';
                        } : undefined)}

                        onSort={(e) => {
                            //zdarzenie jest wywoływane po kliknięciu na nagłówek kolumny tabeli; 
                            // zamiast sortowania wyświetlamy FilterPanel
                            if (filterDialog.current.isVisible()) {
                                filterDialog.current.hide();
                                return;
                            }
                            const config = filterConfig.find(fc => fc.fieldName === e.sortField);
                            const filterPanelState = {
                                timestamp: new Date(),
                                fieldName: e.sortField,
                                filterType: config.type,
                                filterParams: enhanceFilterParamsWithCurrentSelection(e.sortField, config.type, config.params),
                            }
                            setFilterPanelState(filterPanelState);

                        }}
                        sortField={searchCriteria?.sortField} sortOrder={searchCriteria?.sortOrder}
                        paginatorTemplate={{
                            layout: 'PrevPageLink JumpToPageInput CurrentPageReport NextPageLink RowsPerPageDropdown',
                            CurrentPageReport: (options) => {
                                return (
                                    <span>
                                        {t('dataTable.of')} {options.totalPages}
                                    </span>
                                );
                            },
                            JumpToPageInput: (options) => {
                                return (
                                    <span className='jump-to-input'>
                                        <InputText
                                            size="3"
                                            className="ml-1 small"
                                            value={currentPage}
                                            tooltip={pageInputTooltip}
                                            onKeyDown={(e) => onPageInputKeyDown(e, options)}
                                            onBlur={(e) => onPageInputKeyDown({ key: 'Enter', target: e.target }, options)}
                                            onChange={onPageInputChange}
                                            keyfilter='int'
                                        />
                                    </span>
                                );
                            }
                        }}
                        sortIcon={
                            (state) => {
                                return (
                                    <>
                                        <div className='flex'>
                                            <img src={filterIcon} className='ml-3' />
                                            {state.sortOrder === 0 && <img src={sortIcon} className='ml-1' />}
                                            {state.sortOrder === 1 && <img src={sortUpIcon} className='ml-1' />}
                                            {state.sortOrder === -1 && <img src={sortDownIcon} className='ml-1' />}
                                        </div>
                                    </>
                                )
                            }
                        }
                        selection={selectable ? selected : undefined}
                        onSelectionChange={e => {
                            if (selectable) handleSelectionChange(e);
                        }}
                        onRowSelect={onRowSelect}
                        onRowUnselect={onRowUnselect}
                        onRowClick={onRowClick}
                        selectionMode={selectable ? 'checkbox' : undefined}
                        selectAll={selectable ? selectedAll : undefined}
                        selectionPageOnly={selectable ? true : undefined}
                        onSelectAllChange={selectable ? (e => {
                            setSelectedAll(e.checked);
                        }) : undefined}
                    >
                        {selectable && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>}
                        {children}
                    </PrimeReactDataTable>
                </div>

            </div>
        </>
    )
});


const FilterItem = ({ label, removable, className, onClick, ...props }) => {
    return (
        <Chip label={label} className={className} removable={removable} removeIcon={
            () => {
                return (
                    <CloseSmallIcon className='ml-2 cursor-pointer' key={`${props.key}-icon`} onClick={() => {
                        if (removable && onClick) {
                            onClick();
                        }
                    }} />
                )
            }}
            onClick={(e) => {
                if (!removable && onClick) {
                    onClick(e);
                }
            }}
        />
    )
}



export default DataTable;